import classNames from 'classnames';

import React, {
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  RefAttributes,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Icon } from '../icon';
import { Image } from '../image';
import { sleep } from '../lib/sleep';

import styles from './field.scss';
import type { InputMode, Type } from './types';

type LabelProps = {
  label: string;
  value: string;
  placeholder?: string;
  isLeftContent?: boolean;
  placeholderMask?: string;
  error?: string | boolean;
  alwaysActive?: boolean;
  dataTestId?: string;
  isRequired?: boolean;
};

const COMPONENT_ID = 'modulor-field';

const Label: React.FC<LabelProps> = ({
  label,
  value,
  placeholder = '',
  placeholderMask = '',
  error,
  alwaysActive,
  isRequired,
  dataTestId,
  isLeftContent,
}) => {
  const classes = classNames({
    [styles['field-label']]: true,
    [styles['with-left-content-label']]: isLeftContent,
    [styles.active]: value.length > 0 || placeholder.length > 0 || placeholderMask.length > 0,
    [styles.error]: error,
    [styles['always-active']]: alwaysActive,
  });

  const requiredAsterisk = isRequired ? (
    <span className={styles['required-asterisk']}>*</span>
  ) : null;

  return label?.length > 0 ? (
    <label className={classes} data-testid={dataTestId}>
      {label}
      {requiredAsterisk}
    </label>
  ) : null;
};

type ClearProps = {
  disabled?: boolean;
  handleClear?: () => void;
  clearVisible?: boolean;
  value?: string;
  dataTestId?: string;
  focused: boolean;
};

const Clear: React.FC<ClearProps> = ({
  disabled,
  handleClear,
  clearVisible,
  value = '',
  focused,
  dataTestId,
}) => {
  return (
    <Icon
      className={styles.clear}
      name="clear-filled"
      fill="text-body"
      disabled={disabled}
      onClick={handleClear}
      style={{
        display: clearVisible ?? (value.length > 0 && focused) ? 'block' : 'none',
      }}
      onMouseDown={handleClear}
      dataTestId={dataTestId}
    />
  );
};

type PlaceholderMaskProps = {
  focused?: boolean;
  value?: string;
  isLeftContent?: boolean;
  placeholderMask: string;
  dataTestId?: string;
};

const PlaceholderMask: React.FC<PlaceholderMaskProps> = ({
  focused,
  value,
  isLeftContent,
  placeholderMask,
  dataTestId,
}) => {
  return placeholderMask?.length ? (
    <div
      className={classNames(styles.placeholder, {
        [styles['with-left-content-placeholder']]: isLeftContent,
        [styles['focused-placeholder']]: focused || value,
      })}
      data-testid={dataTestId}>
      {placeholderMask}
    </div>
  ) : null;
};

type FieldIconProps = {
  icon: string;
  iconPlaceholder?: string;
  disabled?: boolean;
  onActionIconClick?: () => void;
  preloadIcon?: string;
  dataTestId?: string;
  isLeftIcon?: boolean;
};

const FieldIcon: React.FC<FieldIconProps> = ({
  icon,
  iconPlaceholder,
  disabled,
  onActionIconClick,
  preloadIcon,
  dataTestId,
  isLeftIcon,
}) => {
  return icon?.length > 0 ? (
    <Icon
      className={classNames(styles['content'], {
        [styles['content-left']]: isLeftIcon,
      })}
      name={icon}
      preloadIcon={preloadIcon}
      placeholder={iconPlaceholder}
      fill="text-body"
      disabled={disabled}
      onClick={onActionIconClick}
      dataTestId={dataTestId}
    />
  ) : null;
};

type FieldImageProps = {
  image: string;
  dataTestId?: string;
  isLeftImage?: boolean;
};

const FieldImage: React.FC<FieldImageProps> = ({ image, dataTestId, isLeftImage }) => {
  return image.length > 0 ? (
    <div
      className={classNames(styles['content'], {
        [styles['content-left']]: isLeftImage,
      })}>
      <Image name={image} width="24" height="24" dataTestId={dataTestId} alt={`${image}-image`} />
    </div>
  ) : null;
};

export type InputProps = {
  inputRef: React.RefObject<HTMLInputElement>;
  name?: string;
  label?: string;
  value?: string;
  isLeftContent?: boolean;
  placeholder?: string;
  error?: string | boolean;
  disabled?: boolean;
  icon?: string;
  type: Type;
  pattern?: string;
  inputMode?: InputMode;
  readOnly?: boolean;
  onChange: (value: string) => void;
  onFocus?: React.FocusEventHandler;
  onBlur?: React.FocusEventHandler;
  onKeyUp?: React.KeyboardEventHandler;
  onKeyDown?: React.KeyboardEventHandler;
  maxLength?: number;
  minLength?: number;
  max?: number;
  min?: number;
  step?: number;
  secureText?: boolean;
  onInputMount?: (input: HTMLInputElement) => void;
  dataTestId?: string;
  autoComplete?: string;
  image?: string;
};

const Input: React.FC<InputProps> = ({
  inputRef,
  name = '',
  label = '',
  value = '',
  placeholder = '',
  isLeftContent,
  error,
  disabled = false,
  icon = '',
  type = 'text',
  pattern,
  inputMode,
  readOnly,
  onChange,
  onFocus,
  onBlur,
  onKeyUp,
  onKeyDown,
  maxLength,
  minLength,
  max,
  min,
  step,
  secureText = false,
  onInputMount,
  dataTestId,
  autoComplete,
  image = '',
}) => {
  useEffect(() => {
    if (onInputMount && inputRef) {
      onInputMount(inputRef?.current);
    }
  }, [onInputMount, inputRef]);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { selectionStart, selectionEnd } = inputRef.current;

    const isCursorAtTheEnd = selectionEnd !== inputRef.current.value.length;

    onChange?.(e.target.value);

    // needed to synchronize the inputRef value with the changed value
    await sleep();

    if (isCursorAtTheEnd) {
      inputRef.current.setSelectionRange(selectionStart, selectionEnd);
    }
  };

  const classes = classNames({
    [styles['field-input']]: true,
    [styles['with-left-content']]: isLeftContent,
    [styles['with-label']]: label?.length > 0,
    [styles['with-content']]: icon?.length > 0 || image.length > 0,
    [styles.active]: value.length > 0,
    [styles.disabled]: disabled,
    [styles.error]: error,
  });

  const typePassword = secureText ? 'password' : 'boolean';

  return (
    <input
      ref={inputRef}
      type={type !== 'password' ? type : typePassword}
      className={classes}
      placeholder={placeholder}
      name={name}
      pattern={pattern}
      inputMode={inputMode}
      max={max}
      min={min}
      minLength={minLength}
      maxLength={maxLength}
      step={step}
      readOnly={typeof readOnly === 'boolean' ? readOnly : undefined}
      disabled={disabled}
      value={value}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyUp={onKeyUp}
      onKeyDown={onKeyDown}
      onChange={handleChange}
      data-testid={dataTestId}
      autoComplete={autoComplete}
    />
  );
};

export type FieldProps = RefAttributes<HTMLDivElement> &
  HTMLAttributes<HTMLDivElement> & {
    name?: string;
    label?: string;
    value?: string;
    placeholder?: string;
    error?: string | boolean;
    disabled?: boolean;
    icon?: string;
    type: Type;
    pattern?: string;
    inputMode?: InputMode;
    readOnly?: boolean;
    onChange: (value: string) => void;
    onClearClick?: () => void;
    onActionIconClick?: () => void;
    onFocus?: React.FocusEventHandler;
    onBlur?: React.FocusEventHandler;
    onKeyUp?: React.KeyboardEventHandler;
    onKeyDown?: React.KeyboardEventHandler;
    placeholderMask?: string;
    maxLength?: number;
    minLength?: number;
    max?: number;
    min?: number;
    step?: number;
    iconPlaceholder?: string;
    secureText?: boolean;
    onInputMount?: (input: HTMLInputElement) => void;
    alwaysActive?: boolean;
    clearVisible?: boolean;
    dataTestId?: string;
    isRequired?: boolean;
    preloadIcon?: string;
    leftIcon?: string;
    autoComplete?: string;
    image?: string;
    leftImage?: string;
    hint?: string;
  };

export const Field: ForwardRefExoticComponent<FieldProps> = forwardRef<HTMLDivElement, FieldProps>(
  (
    {
      name = '',
      label = '',
      value = '',
      placeholder = '',
      error,
      disabled = false,
      icon = '',
      type = 'text',
      pattern,
      inputMode,
      readOnly,
      onChange,
      onClearClick,
      onActionIconClick,
      onFocus,
      onBlur,
      onKeyUp,
      onKeyDown,
      placeholderMask = '',
      maxLength,
      minLength,
      max,
      min,
      step,
      iconPlaceholder,
      secureText = false,
      onInputMount,
      alwaysActive = false,
      clearVisible,
      isRequired,
      leftIcon,
      preloadIcon,
      dataTestId = COMPONENT_ID,
      autoComplete = 'off',
      className,
      image = '',
      leftImage = '',
      ...rest
    },
    ref,
  ) => {
    const inputRef = useRef(null);

    const [focused, setFocused] = useState(false);
    const [cleared, setCleared] = useState(false);

    const onInputFocus: React.FocusEventHandler = (e) => {
      onFocus && onFocus(e);
      setFocused(true);
    };

    const onInputBlur: React.FocusEventHandler = (e) => {
      onBlur && onBlur(e);

      if (cleared) {
        inputRef.current.focus();
      } else {
        setFocused(false);
        inputRef.current.blur();
      }

      setCleared(false);
    };

    const handleClear = () => {
      if (onClearClick != null) {
        onClearClick();
        return;
      }

      onChange('');

      if (inputRef.current != null) {
        inputRef.current.value = '';
        setCleared(true);
      }
    };

    const trimmedImage = image.trim();
    const trimmedLeftImage = leftImage.trim();

    return (
      <div
        className={classNames(className, styles.field)}
        ref={ref}
        data-component-name={COMPONENT_ID}
        data-id={dataTestId}
        data-testid={dataTestId}
        {...rest}>
        {leftIcon && (
          <FieldIcon
            isLeftIcon={!!leftIcon}
            icon={leftIcon}
            iconPlaceholder={iconPlaceholder}
            preloadIcon={preloadIcon}
            disabled={disabled}
            onActionIconClick={onActionIconClick}
            dataTestId={`${dataTestId}-icon`}
          />
        )}
        <FieldImage
          isLeftImage={!!trimmedLeftImage}
          image={trimmedLeftImage}
          dataTestId={`${dataTestId}-image`}
        />
        <Input
          inputRef={inputRef}
          name={name}
          label={label}
          value={value}
          isLeftContent={!!leftIcon || !!trimmedLeftImage}
          placeholder={placeholder}
          error={error}
          disabled={disabled}
          icon={icon}
          type={type}
          pattern={pattern}
          inputMode={inputMode}
          readOnly={readOnly}
          onChange={onChange}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          onKeyUp={onKeyUp}
          onKeyDown={onKeyDown}
          maxLength={maxLength}
          minLength={minLength}
          max={max}
          min={min}
          step={step}
          secureText={secureText}
          onInputMount={onInputMount}
          dataTestId={`${dataTestId}-input`}
          autoComplete={autoComplete}
          image={trimmedImage}
        />
        <Label
          label={label}
          value={value}
          placeholder={placeholder}
          placeholderMask={placeholderMask}
          error={error}
          isLeftContent={!!leftIcon || !!trimmedLeftImage}
          alwaysActive={alwaysActive}
          isRequired={isRequired}
          dataTestId={`${dataTestId}-label`}
        />
        <PlaceholderMask
          value={value}
          focused={focused}
          isLeftContent={!!leftIcon || !!trimmedLeftImage}
          placeholderMask={placeholderMask}
          dataTestId={`${dataTestId}-placeholder-mask`}
        />
        <Clear
          disabled={disabled}
          handleClear={handleClear}
          clearVisible={clearVisible}
          value={value}
          focused={focused}
          dataTestId={`${dataTestId}-clear`}
        />
        <FieldIcon
          icon={icon}
          iconPlaceholder={iconPlaceholder}
          preloadIcon={preloadIcon}
          disabled={disabled}
          onActionIconClick={onActionIconClick}
          dataTestId={`${dataTestId}-icon`}
        />
        <FieldImage image={trimmedImage} dataTestId={`${dataTestId}-image`} />
      </div>
    );
  },
);

Field.displayName = 'Field';
